// Iter XX.172 — Focused Entry Chips experiment assignment.
//
// Deterministic 50/50 by browser_id, sticky across sessions and tabs.
// Master kill switch via REACT_APP_FEATURE_FOCUSED_ENTRY. Founder
// override via localStorage.gully_entry_experiment ∈ { "control" | "focused" }.
//
// Mirrors `landingExperiment.js` — deliberately kept as a separate
// module so the two experiments can be turned on/off independently
// and analytics stay cleanly attributable.
import { getBrowserId } from "./landingExperiment";

const STORAGE_KEY = "gully_entry_experiment_v1";
const OVERRIDE_KEY = "gully_entry_experiment";

function _hash(str) {
  // djb2 — matches landingExperiment.js exactly so the two
  // experiments hash independently but predictably.
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return Math.abs(h);
}

/**
 * Return { variant: "control" | "focused", reason } for the current
 * visitor. Sticky per browser_id — the same visitor gets the same
 * variant on every landing paint, tab, and session.
 *
 * Assignment ORDER (any earlier hit wins):
 *   1. Persisted assignment (localStorage.gully_entry_experiment_v1)
 *   2. Master kill switch (REACT_APP_FEATURE_FOCUSED_ENTRY=off → control)
 *   3. Founder override (localStorage.gully_entry_experiment)
 *   4. Hash bucket (browser_id % 100 < 50 → focused, else control)
 */
export function getEntryExperiment({ userId } = {}) {
  // 1. Sticky assignment — persisted on first landing paint.
  try {
    const persisted = localStorage.getItem(STORAGE_KEY);
    if (persisted === "control" || persisted === "focused") {
      return { variant: persisted, reason: "persisted" };
    }
  } catch { /* localStorage disabled — proceed to fresh assign */ }

  // 2. Master kill switch.
  const master = (process.env.REACT_APP_FEATURE_FOCUSED_ENTRY || "on").toLowerCase();
  if (master === "off") {
    return { variant: "control", reason: "flag_off" };
  }

  // 3. Founder override.
  try {
    const o = (localStorage.getItem(OVERRIDE_KEY) || "").toLowerCase();
    if (o === "control" || o === "focused") {
      return { variant: o, reason: "override" };
    }
  } catch { /* ignore */ }

  // 4. Deterministic hash-bucket assignment.
  const key = userId || getBrowserId();
  const bucket = _hash(String(key)) % 100;
  const variant = bucket < 50 ? "focused" : "control";
  return { variant, reason: "assigned" };
}

/**
 * Persist the assigned variant to localStorage so it survives across
 * sessions and tabs. Called ONCE per landing paint after
 * `getEntryExperiment()` resolves.
 */
export function persistEntryVariant(variant) {
  if (variant !== "control" && variant !== "focused") return;
  try { localStorage.setItem(STORAGE_KEY, variant); }
  catch { /* localStorage disabled — assignment reruns cleanly next load */ }
}

/**
 * Read the previously-persisted variant (or null). Used by Chat.jsx
 * to tag downstream funnel events with the same variant that fired
 * `primary_cta_clicked`.
 */
export function readPersistedEntryVariant() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "control" || v === "focused") return v;
  } catch { /* noop */ }
  return null;
}
